import _ from 'lodash';
import store from '@store';
import { ERROR_MESSAGE } from '@constants';
import {
  pushModifierToItem,
  removeModifiers,
  pushGlobalModifiersData,
  clearGlobalModifiersData,
  setGlobalSelectedModifierId,
  setSelectedModifierPath,
  editModifiers,
} from '@reducers/orders';
import { setNotification } from '@reducers/notification';

/**
 * Build a pipe-delimited modifier path string.
 * data: string | null  (selectedModifierPath — a string, not an array)
 */
export const getPath = (data, dataToAppend) => {
  const arrayData = !data ? [] : [data];
  if (dataToAppend) {
    arrayData.push(dataToAppend);
  }
  return arrayData.join('|');
};

/** Returns A–Z alphabet array for the filter bar. */
export const getAlphabets = () => {
  const result = [];
  for (let i = 65; i < 91; i += 1) {
    result.push(String.fromCharCode(i));
  }
  return result;
};

/**
 * Count modifiers in the active global mod group whose name starts with `alphabet`.
 */
export const getCountOfAlphabets = (
  alphabet,
  modifierGroupsHashTable,
  modifiersHashTable,
  globalSelectedModifierId
) => {
  const links = modifierGroupsHashTable[globalSelectedModifierId]?.modifierLinks || [];
  return _.filter(links, d =>
    modifiersHashTable[d.modifierId]?.name
      ?.toLowerCase()
      .startsWith(alphabet.toLowerCase())
  ).length;
};

/**
 * Count modifiers in the active global mod group whose name does NOT start with a-z.
 */
export const getCountWithoutStartingWithAlphabet = (
  modifierGroupsHashTable,
  modifiersHashTable,
  globalSelectedModifierId
) => {
  const links = modifierGroupsHashTable[globalSelectedModifierId]?.modifierLinks || [];
  return _.filter(links, d =>
    /^[^a-z]/i.test(modifiersHashTable[d.modifierId]?.name?.toLowerCase() || '')
  ).length;
};

/**
 * Handle clicking a modifier inside the global modifier panel.
 * Mirrors handleModifierInGlobalModClick from the old implementation.
 * Handles max=1 group clearing + tracks addition in globalModifiersData.
 */
export const handleModifierInGlobalModClick = (modifierGroupId, modifierId) => {
  const { orders } = store.getState();
  const { modifierGroupsHashTable, modifierData, selectedModifierPath } = orders;

  const modifierDataToPush = `${modifierId}-${modifierGroupId}`;
  const modGroup = modifierGroupsHashTable[modifierGroupId];

  // For single-select groups, remove any already-selected modifier in that group first
  if (modGroup?.maximum === 1) {
    const modifiersToRemove = [];
    (modGroup.modifierLinks || []).forEach(d => {
      const existing = `${getPath(selectedModifierPath, `${d.modifierId}-${modGroup.id}`)}`;
      if (modifierData.includes(existing)) {
        modifiersToRemove.push(existing);
      }
    });
    if (modifiersToRemove.length > 0) {
      store.dispatch(removeModifiers(modifiersToRemove));
    }
  }

  const pathToPush = getPath(selectedModifierPath, modifierDataToPush);
  store.dispatch(pushGlobalModifiersData(pathToPush));
  store.dispatch(pushModifierToItem(pathToPush));
};

/**
 * Validate required selections and save the global modifier session.
 * Returns false (and shows notification) if minimum is not met.
 * Returns true on success (caller should close the panel).
 */
export const onGlobalModSave = (activeMenuItemId) => {
  const { orders } = store.getState();
  const {
    modifierData,
    modifierGroupsHashTable,
    globalSelectedModifierId,
    selectedModifierPath,
  } = orders;

  // Collect modifiers selected within this global mod session
  const newArray = [];
  modifierData
    .filter(e => e)
    .forEach(modifier => {
      if (
        modifier.startsWith(getPath(selectedModifierPath)) ||
        modifier.includes('|')
      ) {
        const stripped = modifier.replace(getPath(selectedModifierPath), '');
        if (stripped) {
          newArray.push(stripped);
        }
      }
    });

  const modGroup = modifierGroupsHashTable[globalSelectedModifierId];

  // Validate minimum selections
  if (
    modGroup?.minimum > 0 &&
    modGroup.hiddenMenuId !== activeMenuItemId
  ) {
    const modifierListGroupById = _.groupBy(
      newArray,
      d => d.split('-')[1]?.split('|')[0]
    );
    const selected = (modifierListGroupById[globalSelectedModifierId] || []).length;
    if (selected < modGroup.minimum) {
      store.dispatch(
        setNotification({
          type: 'danger',
          message: ERROR_MESSAGE.SELECT_REQUIRED_MODIFIERS,
        })
      );
      return false;
    }
  }

  // Push the selectedModifierPath as a modifier entry if not already present
  if (selectedModifierPath && !modifierData.includes(selectedModifierPath)) {
    store.dispatch(pushModifierToItem(selectedModifierPath));
  }

  store.dispatch(clearGlobalModifiersData());
  store.dispatch(setGlobalSelectedModifierId(null));
  store.dispatch(setSelectedModifierPath(null));
  store.dispatch(editModifiers({ modifier: '', modifierGroupId: '', modifierName: '' }));
  return true;
};

/**
 * Remove all modifiers belonging to the active global modifier group
 * and reset global modifier state.
 */
export const onGlobalModRemove = () => {
  const { orders } = store.getState();
  const {
    modifierData,
    globalModifiersData,
    globalSelectedModifierId,
    selectedModifierPath,
  } = orders;

  let modifierDataToPush;

  if (!selectedModifierPath) {
    // No nested path — filter out any top-level entry that belongs to this global mod group
    modifierDataToPush = modifierData.filter(e =>
      e.includes('|') ? true : !e.includes(`${globalSelectedModifierId}`)
    );
  } else {
    // Use tracked global modifiers data when available; otherwise filter by group ID
    modifierDataToPush =
      globalModifiersData.length > 0
        ? modifierData.filter(d => !globalModifiersData.includes(d))
        : modifierData.filter(e =>
            !e.includes('|')
              ? true
              : !e.includes(`${globalSelectedModifierId}`)
          );
  }

  store.dispatch(pushModifierToItem(modifierDataToPush));
  store.dispatch(clearGlobalModifiersData());
  store.dispatch(setGlobalSelectedModifierId(null));
  store.dispatch(setSelectedModifierPath(null));
  store.dispatch(editModifiers({ modifier: '', modifierGroupId: '', modifierName: '' }));
};
